import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  IssueReceiptApplicationErrors,
  IssueReceiptResponse,
  PaymentLinkPaymentCreatedEnvelope,
  PaymentLinkPaymentQuery,
  PaymentLinkPaymentSearch,
  PaymentLinkPaymentUpdatedEnvelope,
  PaymentLinkPaymentsQueryBuilder,
  QueryPaymentLinkPaymentsResponse,
  SearchPaymentLinkPaymentsResponse,
  issueReceipt as universalIssueReceipt,
  queryPaymentLinkPayments as universalQueryPaymentLinkPayments,
  searchPaymentLinkPayments as universalSearchPaymentLinkPayments,
  typedQueryPaymentLinkPayments as universalTypedQueryPaymentLinkPayments,
} from './paymentlinks-payments-v1-payment-link-payment-payment-link-payments.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/get-paid' };

export function queryPaymentLinkPayments(
  httpClient: HttpClient
): QueryPaymentLinkPaymentsSignature {
  return () =>
    universalQueryPaymentLinkPayments(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryPaymentLinkPaymentsSignature {
  /**
   * Retrieves a list of up to 100 payment link payments given the specified paging, filtering, and sorting ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language) | [REST](https://dev.wix.com/api/rest/getting-started/api-query-language)).
   */
  (): PaymentLinkPaymentsQueryBuilder;
}

export function typedQueryPaymentLinkPayments(
  httpClient: HttpClient
): TypedQueryPaymentLinkPaymentsSignature {
  return (query: PaymentLinkPaymentQuery) =>
    universalTypedQueryPaymentLinkPayments(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryPaymentLinkPaymentsSignature {
  /** */
  (query: PaymentLinkPaymentQuery): Promise<
    NonNullablePaths<
      QueryPaymentLinkPaymentsResponse,
      | `paymentLinkPayments`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.network`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.maskedCreditCard`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.holderName`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.paymentMethod`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.paymentProvider`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.method`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.order.type`
      | `paymentLinkPayments.${number}.giftCardPaymentLinkPayment.ecomOrderId`
      | `paymentLinkPayments.${number}.ecomCheckout.ecomCheckoutId`
      | `paymentLinkPayments.${number}.ecomOrderCheckout.orderPaymentRequestId`
      | `paymentLinkPayments.${number}.amount`
      | `paymentLinkPayments.${number}.paymentLinkId`
      | `paymentLinkPayments.${number}.type`
      | `paymentLinkPayments.${number}.currency`,
      6
    >
  >;
}

export function searchPaymentLinkPayments(
  httpClient: HttpClient
): SearchPaymentLinkPaymentsSignature {
  return (search: PaymentLinkPaymentSearch) =>
    universalSearchPaymentLinkPayments(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchPaymentLinkPaymentsSignature {
  /**
   * Retrieves a list of up to 100 payment links, given the specified filtering, search expression, sorting, and cursor paging ([SDK](https://dev.wix.com/docs/sdk/articles/work-with-the-sdk/api-query-language) | [REST](https://dev.wix.com/api/rest/getting-started/api-query-language)).
   * @param - Search options.
   */
  (search: PaymentLinkPaymentSearch): Promise<
    NonNullablePaths<
      SearchPaymentLinkPaymentsResponse,
      | `paymentLinkPayments`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.network`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.maskedCreditCard`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.creditCardPaymentMethodData.holderName`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.paymentMethod`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.paymentProvider`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.method`
      | `paymentLinkPayments.${number}.regularPaymentLinkPayment.order.type`
      | `paymentLinkPayments.${number}.giftCardPaymentLinkPayment.ecomOrderId`
      | `paymentLinkPayments.${number}.ecomCheckout.ecomCheckoutId`
      | `paymentLinkPayments.${number}.ecomOrderCheckout.orderPaymentRequestId`
      | `paymentLinkPayments.${number}.amount`
      | `paymentLinkPayments.${number}.paymentLinkId`
      | `paymentLinkPayments.${number}.type`
      | `paymentLinkPayments.${number}.currency`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export function issueReceipt(httpClient: HttpClient): IssueReceiptSignature {
  return (paymentLinkPaymentId: string) =>
    universalIssueReceipt(
      paymentLinkPaymentId,
      // @ts-ignore
      { httpClient }
    );
}

interface IssueReceiptSignature {
  /**
   * Issues a receipt for the specified payment link payment.
   * @param - Payment link payment ID.
   */
  (paymentLinkPaymentId: string): Promise<
    NonNullablePaths<IssueReceiptResponse, `receiptId`, 2> & {
      __applicationErrorsType?: IssueReceiptApplicationErrors;
    }
  >;
}

export const onPaymentLinkPaymentCreated = /*#__PURE__*/ EventDefinition(
  'wix.paymentlinks.payments.v1.payment_link_payment_created',
  true,
  (event: PaymentLinkPaymentCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.transactionDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.regularPaymentLinkPayment.billingAddress' },
            { path: 'entity.giftCardPaymentLinkPayment.billingAddress' },
          ],
        },
      ])
    )
)<PaymentLinkPaymentCreatedEnvelope>();
export const onPaymentLinkPaymentUpdated = /*#__PURE__*/ EventDefinition(
  'wix.paymentlinks.payments.v1.payment_link_payment_updated',
  true,
  (event: PaymentLinkPaymentUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.transactionDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'entity.regularPaymentLinkPayment.billingAddress' },
            { path: 'entity.giftCardPaymentLinkPayment.billingAddress' },
          ],
        },
      ])
    )
)<PaymentLinkPaymentUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressLocation,
  AddressStreetOneOf,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarResult,
  AggregationType,
  BaseEventMetadata,
  BulkUpsertPaymentLinkPaymentsRequest,
  BulkUpsertPaymentLinkPaymentsResponse,
  Cashier,
  CreditCardPaymentMethodData,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  DateHistogramAggregation,
  DateHistogramResult,
  DateHistogramResults,
  DomainEvent,
  DomainEventBodyOneOf,
  Ecom,
  EcomCheckout,
  EcomOrderCheckout,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  FullAddressContactDetails,
  GiftCardPaymentLinkPayment,
  GroupByValueResults,
  IdentificationData,
  IdentificationDataIdOneOf,
  IncludeMissingValuesOptions,
  Interval,
  IssueReceiptRequest,
  IssueReceiptResponse,
  LeanCheckout,
  MessageEnvelope,
  MissingValues,
  Mode,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  OrderType,
  PagingMetadataV2,
  PaymentLinkPayment,
  PaymentLinkPaymentCheckoutTypeOneOf,
  PaymentLinkPaymentCreatedEnvelope,
  PaymentLinkPaymentEcomCheckoutTypeOneOf,
  PaymentLinkPaymentPaymentOneOf,
  PaymentLinkPaymentQuerySpec,
  PaymentLinkPaymentSearchSpec,
  PaymentLinkPaymentType,
  PaymentLinkPaymentUpdatedEnvelope,
  PaymentLinkPaymentsQueryBuilder,
  PaymentLinkPaymentsQueryResult,
  PaymentMethod,
  PaymentOrder,
  PaymentOrderTypeDetailsOneOf,
  QueryPaymentLinkPaymentsRequest,
  QueryPaymentLinkPaymentsResponse,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  RegularPaymentLinkPayment,
  RegularPaymentLinkPaymentPaymentMethodDataOneOf,
  RestoreInfo,
  Results,
  ScalarAggregation,
  ScalarResult,
  ScalarType,
  SearchDetails,
  SearchPaymentLinkPaymentsRequest,
  SearchPaymentLinkPaymentsResponse,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  StandardCheckout,
  StreetAddress,
  Subdivision,
  SubdivisionType,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  VatId,
  VatType,
  WalletPaymentMethodData,
  WebhookIdentityType,
  utils,
} from './paymentlinks-payments-v1-payment-link-payment-payment-link-payments.universal.js';
